"use client";

/**
 * One-shot landing config fetch on mount (no polling — scale-to-zero safe).
 * Public site is store-only: waitlist/beta variants are clamped to launch
 * so the homepage never flashes a waitlist form before store CTAs.
 *
 * Never read `window` / `location` during render — that caused Navbar CTA
 * hydration mismatches when `?preview_variant=` differed from the SSR default.
 * Preview overrides apply only after mount (same as config fetch).
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  DEFAULT_LANDING_CONFIG,
  STRATEGY_BOOTSTRAP_LANDING_CONFIG,
  fetchLandingConfig,
  readPreviewVariant,
  toPublicLandingConfig,
  type LandingConfigDocument,
  type LandingVariant,
} from "@/lib/landing-config";
import { setAnalyticsCampaignContext } from "@/lib/analytics";

type LandingConfigContextValue = {
  config: LandingConfigDocument;
  /** Effective variant after preview override. */
  variant: LandingVariant;
  loading: boolean;
};

const LandingConfigContext = createContext<LandingConfigContextValue>({
  config: STRATEGY_BOOTSTRAP_LANDING_CONFIG,
  variant: STRATEGY_BOOTSTRAP_LANDING_CONFIG.variant,
  loading: true,
});

export function useLandingConfig(): LandingConfigContextValue {
  return useContext(LandingConfigContext);
}

export function LandingConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<LandingConfigDocument>(
    STRATEGY_BOOTSTRAP_LANDING_CONFIG,
  );
  const [loading, setLoading] = useState(true);
  /** null until mounted — keeps SSR and first client render identical. */
  const [preview, setPreview] = useState<LandingVariant | null>(null);
  const [previewReady, setPreviewReady] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    fetchLandingConfig(controller.signal)
      .then((result) => {
        if (cancelled || result.status === "aborted") return;
        // ok → live strategy; fallback → launch default only when fetch failed
        const raw =
          result.status === "ok" ? result.config : DEFAULT_LANDING_CONFIG;
        const applied = toPublicLandingConfig(raw);
        setConfig(applied);
        setAnalyticsCampaignContext({
          landing_variant: applied.variant,
          utm_campaign: applied.utmCampaignKey,
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    const syncPreviewFromLocation = () => {
      const nextPreview = readPreviewVariant(window.location.search);
      setPreview(nextPreview);
      setPreviewReady(true);
    };
    syncPreviewFromLocation();
    window.addEventListener("popstate", syncPreviewFromLocation);
    return () => window.removeEventListener("popstate", syncPreviewFromLocation);
  }, []);

  // Until preview has been read post-mount, ignore URL so SSR == CSR.
  const variant: LandingVariant = previewReady
    ? (preview ?? config.variant)
    : config.variant;

  const value = useMemo(
    () => ({ config, variant, loading }),
    [config, variant, loading],
  );

  return (
    <LandingConfigContext.Provider value={value}>
      {children}
    </LandingConfigContext.Provider>
  );
}
